import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { Link } from '~/components/Link'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { CheckListItemType } from '~/redux/apis/types'

import useGetGradePageRoute from '~/dashboard/hooks/useGetGradePageRoute'
import useGetGradeOverviewPageRoute from '~/dashboard/hooks/useGetGradeOverviewPageRoute'

import { useClickActions } from '../hooks/useClickActions'

import { checklistLinks } from '../constants'

import BadgeXP from './BadgeXP'
import CheckBox from './CheckBox'
import BadgeGems from './BadgeGems'

interface Props {
  checklistItem: CheckListItemType
  itemKey: string
}

export default function CheckListItemComponent({ checklistItem, itemKey }: Props): React.ReactElement | null {
  const router = useRouter()
  const { t } = useTranslation(['dashboard', 'generics'])

  const { isCompleted, isVisible, xp, gems } = checklistItem

  const { handleClick } = useClickActions(itemKey)
  const { data } = useGetCurrentGradeQuery()
  const curriculumAvailable = data?.grade !== null
  const curriculumGradeOverviewUrl = useGetGradeOverviewPageRoute({ grade: data?.grade ?? '' })

  const selectGradeUrl: string = useGetGradePageRoute()

  const getChecklistLink = (key: string): string => {
    if (key === 'watchVideo' || key === 'completeCurriculumActivity') {
      return curriculumAvailable ? curriculumGradeOverviewUrl : selectGradeUrl
    }
    return checklistLinks[key] || ''
  }

  const checklistLink = getChecklistLink(itemKey)

  const handleCheckBoxClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (checklistLink) {
      router.push(checklistLink)
    } else {
      handleClick(event)
    }
  }

  if (!isVisible || itemKey === REWARD_FEATURE.COMPLETED_CHECKLIST) return null

  return (
    <div className="my-1.5 flex flex-row justify-between items-center">
      <div className="flex flex-row justify-between items-center">
        <CheckBox isCompleted={isCompleted} onCheckboxClick={handleCheckBoxClick} />
        {checklistLink ? (
          <Link
            className={`text-xs font-normal ml-2 ${isCompleted ? 'opacity-25 pointer-events-none' : ''}`}
            onClick={handleClick}
            to={checklistLink}
          >
            {t(`dashboard:checklists.${itemKey}`)}
          </Link>
        ) : (
          <button
            className={`text-xs text-start inline-block leading-none font-normal ml-2 ${
              isCompleted ? 'opacity-25 pointer-events-none' : ''
            }`}
            onClick={handleClick}
          >
            {t(`dashboard:checklists.${itemKey}`)}
          </button>
        )}
      </div>
      <div className="flex flex-row justify-between items-center">
        {gems && <BadgeGems className="bg-pink-light ml-2" gems={gems} />}
        {xp && <BadgeXP className="bg-pink-light" xp={xp} />}
      </div>
    </div>
  )
}
